import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import Modal from '@/components/ui/Modal';

/**
 * Phase 12.1 — reusable TOTP code prompt for sensitive operations
 * (create / archive / modify workspace and members).
 *
 * Three modes driven by `errorCode`:
 *   - 'TOTP_REQUIRED'      — caller has not enrolled 2FA. Show settings link
 *                            instead of code input.
 *   - 'TOTP_CODE_REQUIRED' / 'TOTP_INVALID' / null  — show 6-digit input.
 *
 * UX:
 *   - Auto-focus input on open
 *   - Submit on Enter when 6 digits entered
 *   - Resets entered code when error changes (so user sees fresh field
 *     after invalid attempt)
 */
export interface TotpModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the 6-digit code. Throwing/rejecting keeps the modal open. */
  onSubmit: (code: string) => Promise<void> | void;
  /** Backend error code from previous attempt; null if first attempt. */
  errorCode?: 'TOTP_REQUIRED' | 'TOTP_CODE_REQUIRED' | 'TOTP_INVALID' | null;
  errorMessage?: string | null;
  loading?: boolean;
  /** Verb shown in the title, e.g. «Создание workspace». */
  actionLabel?: string;
}

export default function TotpModal({
  open, onClose, onSubmit, errorCode, errorMessage, loading, actionLabel,
}: TotpModalProps) {
  const [code, setCode] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset code whenever error changes (so user starts fresh).
  useEffect(() => {
    if (errorCode === 'TOTP_INVALID') setCode('');
  }, [errorCode]);

  // Reset code when modal closes.
  useEffect(() => {
    if (!open) setCode('');
  }, [open]);

  // Auto-focus input on open / when becoming code-input mode.
  useEffect(() => {
    if (open && errorCode !== 'TOTP_REQUIRED') {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open, errorCode]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (code.length !== 6 || loading) return;
    await onSubmit(code);
  };

  // Mode A: enrollment required.
  if (errorCode === 'TOTP_REQUIRED') {
    return (
      <Modal open={open} onClose={onClose} title="Двухфакторка не настроена">
        <div className="space-y-4 text-sm">
          <div className="flex gap-3">
            <ShieldAlert className="text-amber-400 flex-shrink-0 mt-0.5" size={20} />
            <p className="text-surface-300">
              Эта операция требует двухфакторной аутентификации. Включите её
              в настройках профиля и попробуйте снова.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={onClose} className="btn-ghost">Отмена</button>
            <Link to="/settings" onClick={onClose} className="btn-primary inline-flex">
              Включить 2FA
            </Link>
          </div>
        </div>
      </Modal>
    );
  }

  // Mode B: prompt for code.
  return (
    <Modal open={open} onClose={onClose} title={actionLabel ?? 'Подтверждение действия'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-surface-300">
          Введите 6-значный код из приложения двухфакторки.
        </p>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="irc-input text-center text-2xl tracking-[0.4em] font-mono"
          placeholder="000000"
          disabled={loading}
        />
        {errorMessage && (
          <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2">
            {errorMessage}
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-ghost" disabled={loading}>
            Отмена
          </button>
          <button
            type="submit"
            disabled={code.length !== 6 || loading}
            className="btn-primary"
          >
            {loading ? 'Проверяем…' : 'Подтвердить'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
